import React, { Component } from 'react';
import styled from 'styled-components';
import { system } from './system';
import Label from './Label';
import Field from './Field';
import { FormError } from './Input';

// Replicated from molekule/lib/Form/Select.js (native select, not react-select)
// Call sites:
//   Table.js / TableToggle.js:
//     <Select size="sm" style={{width:70}} name="pageSize"
//             options={[...]} value={pageSize.toString()}
//             onChange={(_, value) => { ... }} />
//
// onChange API: (name, value)
// onBlur API: (name)

interface SelectSys {
  [key: string]: any;
}

interface SelectContainerProps {
  $value?: string | number;
  $size?: string;
  $sys?: SelectSys;
}

const SelectContainer = styled.div<SelectContainerProps>`
  background: white;
  border: 1px solid
    ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.greyLight : '#E8E9EA'};
  height: 48px;
  outline: none;
  width: 100%;
  position: relative;
  border-radius: ${(p: any) =>
    p.theme && p.theme.radius != null ? `${p.theme.radius}px` : '8px'};
  transition: 250ms all;
  -webkit-appearance: none;
  font-family: inherit;
  font-size: ${(p: any) =>
    p.theme && p.theme.typography
      ? `${p.theme.typography.fontSize}px`
      : '16px'};
  vertical-align: middle;
  display: flex;
  align-items: center;

  &:hover {
    border-color: ${(p: any) =>
      p.theme && p.theme.colors ? p.theme.colors.greyDark : '#A4A6AA'};
  }

  ${(p: any) => system(p.$sys, p.theme)}
`;

interface SelectInputProps {
  $value?: string | number;
  $isFloating?: boolean;
}

const SelectInput = styled.select<SelectInputProps>`
  position: relative;
  z-index: 2;
  padding: 0 32px 0 8px;
  outline: none;
  width: 100%;
  height: 100%;
  font-size: ${(p: any) =>
    p.theme && p.theme.typography
      ? `${p.theme.typography.fontSize}px`
      : '16px'};
  font-family: inherit;
  background: transparent;
  color: ${(p: SelectInputProps & { theme?: any }) =>
    p.$value
      ? p.theme && p.theme.typography
        ? p.theme.typography.color
        : '#1B202B'
      : p.theme && p.theme.colors
        ? p.theme.colors.greyDarker
        : '#767980'};
  border: none;
  -webkit-appearance: none;
  cursor: pointer;

  ${(p: SelectInputProps) => (p.$isFloating ? `margin-top: 16px;` : '')}
`;

// Simple down-arrow icon using CSS border trick — no icon library dependency
const ArrowIcon = styled.div`
  position: absolute;
  height: 100%;
  right: 0;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  padding-right: 8px;
  pointer-events: none;

  &::after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid
      ${(p: any) =>
        p.theme && p.theme.colors ? p.theme.colors.greyDarker : '#767980'};
  }
`;

export interface SelectOption {
  value: string | number;
  label: string | number;
}

export interface SelectProps {
  name: string;
  id?: string;
  options: SelectOption[];
  value?: string | number;
  placeholder?: string;
  label?: string;
  error?: string;
  size?: string;
  floating?: boolean;
  onChange?: (name: string, value: string) => void;
  onBlur?: (name: string) => void;
  className?: string;
  style?: React.CSSProperties;
  [key: string]: any;
}

interface SelectState {
  value: string | number;
}

class Select extends Component<SelectProps, SelectState> {
  static defaultProps = {
    onChange: () => {},
    onBlur: () => {},
    options: [],
  };

  constructor(props: SelectProps) {
    super(props);
    this.state = {
      value: props.value != null ? props.value : '',
    };
  }

  static getDerivedStateFromProps(props: SelectProps, state: SelectState) {
    if (props.value !== undefined && props.value !== state.value) {
      return { value: props.value };
    }
    return null;
  }

  handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    this.setState({ value: newValue });
    if (this.props.onChange) {
      this.props.onChange(e.target.name, newValue);
    }
  };

  handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    if (this.props.onBlur) {
      this.props.onBlur(e.target.name);
    }
  };

  render() {
    const {
      id,
      name,
      options,
      placeholder,
      error,
      size,
      label,
      floating,
      className,
      style,
      onChange: _onChange,
      onBlur: _onBlur,
      value: _value,
      ...rest
    } = this.props;

    // Strip non-DOM props
    const { styles, $sys, forwardedRef, ...domRest } = rest as any;

    const { value } = this.state;
    const isFloating = floating && value != null && `${value}`.trim() !== '';

    return (
      <Field className={className}>
        {label && !floating && <Label size={size}>{label}</Label>}
        <SelectContainer $value={value} $size={size} style={style} {...domRest}>
          {label && isFloating && (
            <Label
              htmlFor={id}
              size={size}
              isFloating={true}
              isFloatable={floating}
            >
              {label}
            </Label>
          )}
          <SelectInput
            $value={value}
            $isFloating={!!isFloating}
            id={id}
            name={name}
            value={value}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
          >
            <option value="">{placeholder || 'Select an option...'}</option>
            {(options || []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectInput>
          <ArrowIcon aria-hidden="true" />
        </SelectContainer>
        {error && <FormError>{error}</FormError>}
      </Field>
    );
  }
}

export default Select;
